import { Calendar, Space, Button, Alert, Tooltip, Spin, Select, Row, Col } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/lt";
import locale from "antd/es/date-picker/locale/lt_LT";
import CardPartial from "../partials/Card";
import type { Dayjs } from "dayjs";

interface City {
  uuid: string;
  name: string;
}

interface Step1Props {
  cities: City[];
  selectedCity: City | null;
  availableTimes: any[];
  activeTime: string | null;
  loading: boolean;
  onCityChange: (city: City) => void;
  onCalendarChange: (date: Dayjs) => void;
  onTimeSelect: (timeUuid: string, basePrice: number) => void;
  calendarFilter: (current: Dayjs) => boolean;
  nonCriticalError: boolean;
  onCalendarErrorReset: () => void;
  isPosting: boolean;
  errors: { [key: string]: string };
}

const Step1: React.FC<Step1Props> = ({
  cities,
  selectedCity,
  availableTimes,
  activeTime,
  loading,
  onCityChange,
  onCalendarChange,
  onTimeSelect,
  calendarFilter,
  nonCriticalError,
  onCalendarErrorReset,
  isPosting,
  errors,
}) => {
  const startDate = dayjs().add(1, "day");

  return (
    <>
      {nonCriticalError && (
        <Alert
          type="error"
          message="Nepavyko įkelti laiko šiai datai. Kalendorius buvo atstatytas į šiandieną."
          action={
            <Button type="primary" onClick={onCalendarErrorReset}>
              Atstatyti Kalendorių
            </Button>
          }
          banner
          closable
        />
      )}

      {errors["city_uuid"] && (
        <Alert
          type="error"
          message={errors["city_uuid"]}
          banner
          style={{ marginBottom: "8px" }}
        />
      )}

      <CardPartial
        isPosting={isPosting}
        active_time={"none"}
      >
        <div style={{ textAlign: "center" }}>
          <Space>
            {cities.map((city) => (
            <Button
              type={selectedCity?.uuid === city.uuid ? "primary" : "default"}
              key={`${city.uuid}-${city.name}`}
              onClick={() => onCityChange(city)}
            >
              {city.name}
            </Button>
            ))}
          </Space>
        </div>
      </CardPartial>

      {selectedCity ? (
        <>
          <CardPartial
            isPosting={isPosting}
            active_time={"none"}
          >
            <div style={{ textAlign: "center" }}>
              Pasirinktas miestas:{" "}
              <span style={{ fontWeight: "bold" }}>{selectedCity.name}</span>
            </div>
          </CardPartial>

          <CardPartial isPosting={isPosting} active_time={"none"}>
            <Spin tip="Kraunasi" size="large" spinning={loading}>
              <Tooltip title="Visą dieną užimta">
                <Calendar
                  onSelect={onCalendarChange}
                  defaultValue={startDate}
                  disabledDate={calendarFilter}
                  locale={locale}
                  headerRender={({ value, type, onChange }) => {
                    // Custom calendar header
                    const start = 0;
                    const end = 12;
                    const monthOptions = [];
                    let current = value.clone();
                    const localeData = value.localeData();
                    const months = [];
                    for (let i = 0; i < 12; i++) {
                      current = current.month(i);
                      months.push(localeData.months(current));
                    }

                    for (let i = start; i < end; i++) {
                      monthOptions.push(
                        <Select.Option
                          key={i}
                          value={i}
                          className="month-item"
                        >
                          {months[i].toString().charAt(0).toUpperCase() +
                            months[i].toString().slice(1)}
                        </Select.Option>
                      );
                    }

                    const year = value.year();
                    const month = value.month();
                    const options = [];
                    for (let i = year - 10; i < year + 10; i += 1) {
                      options.push(
                        <Select.Option
                          key={i}
                          value={i}
                          className="year-item"
                        >
                          {i}
                        </Select.Option>
                      );
                    }
                    const goToPrevMonth = () => {
                      const newValue = value.clone().subtract(1, "month");
                      onChange(newValue);
                    };

                    const goToNextMonth = () => {
                      const newValue = value.clone().add(1, "month");
                      onChange(newValue);
                    };

                    return (
                      <div style={{ padding: 8 }}>
                        <Row gutter={8} justify={"space-between"} align={"middle"}>
                          <Col>
                            <Button
                              size="small"
                              onClick={goToPrevMonth}
                              icon={<LeftOutlined />}
                            />
                          </Col>
                          <Col>
                            <Space size={4}>
                              <Select
                                size="small"
                                className="my-year-select"
                                value={year}
                                style={{ minWidth: "80px" }}
                                onChange={(newYear) => {
                                  const now = value.clone().year(newYear);
                                  onChange(now);
                                }}
                              >
                                {options}
                              </Select>
                              <Select
                                size="small"
                                value={month}
                                style={{ minWidth: "100px" }}
                                onChange={(newMonth) => {
                                  const now = value.clone().month(newMonth);
                                  onChange(now);
                                }}
                              >
                                {monthOptions}
                              </Select>
                            </Space>
                          </Col>
                          <Col>
                            <Button
                              size="small"
                              onClick={goToNextMonth}
                              icon={<RightOutlined />}
                            />
                          </Col>
                        </Row>
                      </div>
                    );
                  }}
                  validRange={[dayjs(), dayjs().add(30, "month")]}
                  fullscreen={false}
                />
              </Tooltip>
            </Spin>
          </CardPartial>

          {errors["day_selected"] && (
            <Alert
              type="error"
              message={errors["day_selected"]}
              banner
              style={{ marginBottom: "8px" }}
            />
          )}
        </>
      ) : (
        <Alert
          type="warning"
          message="Pirma pasirinkite miestą, kad matytumėte kalendorių"
          banner
        />
      )}

      <CardPartial
        isPosting={isPosting}
        title="Laisvi laikai"
        active_time={"none"}
      >
        <Space
          style={{ width: "100%", justifyContent: "center" }}
          wrap
          direction="horizontal"
        >
          {selectedCity === null ? (
            <Alert
              type="warning"
              message="Pirma pasirinkite miestą, kad matytumėte laisvus laikus"
              banner
            />
          ) : availableTimes.length <= 0 ? (
            <Alert
              type="info"
              message="Jūsų pasirinktai datai nėra laisvų laikų, pasirinkite kitą datą"
              banner
            />
          ) : (
            availableTimes.map((time) => (
              <Button
                type={activeTime === time.uuid ? "primary" : "default"}
                key={time.uuid}
                onClick={() => onTimeSelect(time.uuid, time.base_price)}
                size="small"
              >
                {time.time_start.substring(0, time.time_start.length - 3)}-
                {time.time_end.substring(0, time.time_end.length - 3)}
              </Button>
            ))
          )}
        </Space>

        {errors["time_selected"] && (
          <Alert
            type="error"
            message={errors["time_selected"]}
            banner
            style={{ marginTop: "8px" }}
          />
        )}
      </CardPartial>

    </>
  );
};

export default Step1;